import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { IconGraphy, Typography, colors, Table, Badge, Dropdown, Loading } from '@leapeasy/ui-kit';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { openDetails } from 'store/actions/uiActions';
import { DetailCard } from './DetailCard';
import { getDelegationDetailAction } from 'store/actions/userActions';
import styled from 'styled-components';
import { failedNotif } from 'store/actions/notifActions';
import { useListQueryState } from 'hooks/useListQueryState';
import { getBuilding } from 'store/sagas/APIs/user';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildPropertyListFilterChips,
  parsePropertyListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import { BOOLEAN_YES_NO_OPTIONS, formatBooleanYesNo } from 'utils/formatBooleanYesNo';
import { GlobalUXState } from 'components/GlobalUXState';
import { DetailPageShell } from 'components/DetailPageState';
import { useEntityDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';
import { useBuildingListFetch } from 'hooks/useBuildingListFetch';
import { fitContentTextColumnOptions } from 'utils/fitContentTextColumnOptions';
import { useInvitePropertyPerUserFetch } from 'hooks/useInvitePropertyPerUserFetch';
import { useStableKey } from 'hooks/useStableKey';
import { buildMaskedUserBuildingRow, maskDemoField } from 'utils/demoMask';

const EMPTY_USER_DETAIL_BUILDING_FILTER = {
  options: {},
  searchText: '',
  searchPlaceholder: 'Search Property..',
};

export const UserDetail = ({ route }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { delegationId } = useParams();

  const [tableData, setTableData] = useState([]);
  const { filter, setFilter, sortOptions, setSortOptions, pagination, setPagination } =
    useListQueryState({
      defaultFilter: {
        options: {},
        searchText: '',
        searchPlaceholder: 'Search Property..',
      },
    });
  const [selectedBType, selSelectedBType] = useState([]);
  const [isSHouse, setIsSHouse] = useState([]);

  const buildings = useSelector((state) => state.getIn(['property', 'building']));
  const buildingsJSON = useImmutableJs(buildings);
  const userDetailTableRef = useRef(null);
  const [userDetailExportOpen, setUserDetailExportOpen] = useState(false);
  const [userDetailFullExportTotal, setUserDetailFullExportTotal] = useState(undefined);
  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));
  const legacyDetails = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const legacyDetailsType = legacyDetails ? legacyDetails.get('type') : null;
  const legacyDetailsData = legacyDetails ? legacyDetails.get('data') : null;
  const delegationDetail =
    useSelector((state) => state.getIn(['user', 'delegation_detail'])) || null;
  const delegationDetailJSON = useImmutableJs(delegationDetail);
  const legacyFallbackJSON = useImmutableJs(legacyDetailsData);
  const delegationDetailLoading = useSelector((state) =>
    state.getIn(['user', 'delegation_detail_loading']),
  );
  const delegationDetailError = useSelector((state) =>
    state.getIn(['user', 'delegation_detail_error']),
  );
  const hasUiFallback = !delegationId && legacyDetailsType === 'user' && legacyDetailsData;
  const {
    data: activeDetail,
    isLoading: detailLoading,
    hasError: detailHasError,
  } = useEntityDetailPageState({
    entityId: delegationId,
    entityData: delegationDetailJSON ?? legacyFallbackJSON,
    isLoading: delegationDetailLoading,
    hasError: delegationDetailError,
    hasUiFallback,
  });

  const propertyKey = useStableKey(activeDetail?.property);

  useEffect(() => {
    const pid = activeDetail?.property;
    if (!pid) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await getBuilding({
          id: pid,
          filter: EMPTY_USER_DETAIL_BUILDING_FILTER,
          offset: 0,
          limit: 1,
          sortOptions,
          selectedBType: [],
          isSHouse: [],
        });
        if (!cancelled && response?.total != null) {
          setUserDetailFullExportTotal(Number(response.total));
        }
      } catch (e) {
        // ignore prefetch failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyKey, sortOptions]);

  const mapDelegationBuildingRow = (building, index) => [
    building.name,
    building.building_type,
    building.phone,
    building.billingStreet,
    building.email_address,
    building.student_housing,
    index,
  ];

  const handleUserDetailRequestExportData = useCallback(async () => {
    const pid = activeDetail?.property;
    if (!pid) return [];
    const totalRecords = Number(buildingsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getBuilding({
            id: pid,
            filter,
            offset,
            limit,
            sortOptions,
            selectedBType,
            isSHouse,
          }),
      });
      return rows.map((b, i) => mapDelegationBuildingRow(b, i));
    } catch (e) {
      throw new Error('Unable to export filtered properties right now.');
    }
  }, [activeDetail?.property, buildingsJSON?.total, filter, sortOptions, selectedBType, isSHouse]);

  const handleUserDetailRequestFullExportData = useCallback(async () => {
    const pid = activeDetail?.property;
    if (!pid) return [];
    const totalRecords = Number(userDetailFullExportTotal) || Number(buildingsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getBuilding({
            id: pid,
            filter: EMPTY_USER_DETAIL_BUILDING_FILTER,
            offset,
            limit,
            sortOptions,
            selectedBType: [],
            isSHouse: [],
          }),
      });
      return rows.map((b, i) => mapDelegationBuildingRow(b, i));
    } catch (e) {
      throw new Error('Unable to export all properties right now.');
    }
  }, [activeDetail?.property, userDetailFullExportTotal, buildingsJSON?.total, sortOptions]);

  useBuildingListFetch({
    propertyId: activeDetail?.property,
    filter,
    pagination,
    sortOptions,
    selectedBType,
    isSHouse,
    enabled: Boolean(activeDetail?.property) && !detailLoading && !detailHasError,
  });

  useInvitePropertyPerUserFetch(activeDetail, {
    enabled: Boolean(activeDetail) && !detailLoading && !detailHasError,
  });

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goDetailPage = (index) => {
    const selectedBuilding = buildingsJSON?.data?.[index];
    const buildingId = selectedBuilding?.building_id;
    if (selectedBuilding && buildingId) {
      const detailPath = route
        ? `/user/${route}/detail/buildingDetail/${buildingId}/report`
        : `/property/properties/${buildingId}/detail/report`;
      navigate(detailPath);
      dispatch(
        openDetails({
          type: 'property',
          data: selectedBuilding,
        }),
      );
      return;
    }
    dispatch(failedNotif('Property detail ID is missing.'));
  };
  useEffect(() => {
    const data = [];
    if (buildings) {
      (buildings.toJS()?.data ?? []).forEach((building, value) => {
        data.push(buildMaskedUserBuildingRow(building, isDemo, value));
      });
    }
    setTableData(data);
  }, [buildings, isDemo]);

  useEffect(() => {
    if (!delegationId) {
      if (!legacyDetails || legacyDetailsType !== 'user') {
        dispatch(failedNotif('User detail ID is missing.'));
        onClickBack();
      }
      return;
    }
    dispatch(getDelegationDetailAction(delegationId));
  }, [dispatch, delegationId, legacyDetails, legacyDetailsType, onClickBack]);

  const resetFunc = useCallback(() => {
    selSelectedBType([]);
    setIsSHouse([]);
  }, []);

  const applyFunc = useCallback(() => {}, []);

  const listFilterChips = useMemo(
    () =>
      buildPropertyListFilterChips({
        selectedBType,
        isSHouse,
        unitFilter: null,
      }),
    [selectedBType, isSHouse],
  );

  const handleRemoveUserDetailBuildingFilterChip = useCallback((chipId) => {
    const parsed = parsePropertyListFilterChipId(chipId);
    if (!parsed) return;
    if (parsed.type === 'propertyType') {
      selSelectedBType((prev) => prev.filter((s) => s !== parsed.value));
      return;
    }
    if (parsed.type === 'studentHousing') {
      setIsSHouse((prev) => prev.filter((s) => s !== parsed.value));
    }
  }, []);

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveUserDetailBuildingFilterChip,
    resetFunc,
  );

  const delegatedBuildingColumns = [
    {
      name: 'Name',
      options: {
        ...fitContentTextColumnOptions(),
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'name',
    },
    {
      name: 'Property Type',
      options: {
        flex: '80px 1 1',
        customBodyRenderer: (value) => (
          <Badge
            background="rgba(243, 241, 244, 1)"
            color={value ? 'purple' : 'tomato'}
            label={
              !value
                ? 'undefined'
                : value === 'Auto Enroll'
                  ? 'AE'
                  : value === 'Event Process'
                    ? 'EP'
                    : 'AE EP'
            }
            rounded
            textSize="medium"
          />
        ),
        sort: true,
      },
      key: 'building_type',
    },
    {
      name: 'Phone Number',
      options: {
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'phone',
    },
    {
      name: 'Address',
      options: {
        flex: '170px 1 1',
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'billingStreet',
    },
    {
      name: 'Email Address',
      options: {
        flex: '120px 1 1',
        sort: true,
        customBodyRenderer: (value, _) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'email_address',
    },
    {
      name: 'Student Housing',
      options: {
        flex: '40px 1 1',
        filter: true,
        sort: true,
        customBodyRenderer: (value) => (
          <Badge
            background="#F3F1F4"
            color={value === 'true' ? 'parisGreen' : 'tomato'}
            label={formatBooleanYesNo(value)}
            rounded
            textSize="medium"
          />
        ),
        filterOptions: BOOLEAN_YES_NO_OPTIONS,
      },
      key: 'student_housing',
    },
    {
      name: 'Detail',
      options: {
        flex: '5px 1 1',
        sort: true,
        customBodyRenderer: (value, tableMeta) => (
          <IconGraphy
            icon={'FileFolder.Description'}
            style={{ color: '#702572' }}
            onClick={() => goDetailPage(value)}
          />
        ),
      },
      key: 'detail',
    },
  ];

  const user = activeDetail;

  const onRetry = useCallback(() => {
    if (delegationId) dispatch(getDelegationDetailAction(delegationId));
  }, [delegationId, dispatch]);

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={user ? maskDemoField(isDemo, 'username', user.accepter.username) : null}
        isLoading={detailLoading}
        hasError={detailHasError}
        onRetry={onRetry}
        loadingLabel="Loading user details..."
        skeletonVariant="fields"
      >
        {user ? <DetailCard user={user} isDemo={isDemo} /> : null}
      </DetailPageShell>

      {user && !detailLoading && !detailHasError && buildings && (
        <React.Fragment>
          <Box
            border="1px solid #EAEAEA"
            sx={{ background: 'white', marginTop: '8px' }}
            borderRadius="12px"
          >
            <React.Fragment>
              <CustomBox>
                {buildingsJSON.isLoading && (
                  <LoadingEffect>
                    <Loading size={'large'} />
                  </LoadingEffect>
                )}
                <Table
                  showEmptyState={!buildingsJSON.isLoading}
                  emptyContent={
                    <GlobalUXState
                      variant={filter?.searchText ? 'noResults' : 'noData'}
                      compact
                      description={
                        filter?.searchText
                          ? 'No properties match your search.'
                          : 'No properties available for this user.'
                      }
                    />
                  }
                  ref={userDetailTableRef}
                  onExportButtonClick={() => setUserDetailExportOpen(true)}
                  onRequestExportData={
                    activeDetail?.property ? handleUserDetailRequestExportData : undefined
                  }
                  onRequestFullExportData={
                    activeDetail?.property ? handleUserDetailRequestFullExportData : undefined
                  }
                  data={tableData}
                  filter={filter}
                  onChangeFilter={setFilter}
                  onChangeRowsPerPage={(value) =>
                    setPagination({
                      ...pagination,
                      rowsPerPage: value,
                      pageNumber: 0,
                    })
                  }
                  dropFilterActiveSummary={dropFilterActiveSummary}
                  dropFilterActiveCount={dropFilterActiveCount}
                  dropFilter={{
                    component: (
                      <DropFilterContainer>
                        <Dropdown
                          onChange={(e) => {
                            selSelectedBType(e.target.value);
                          }}
                          options={[
                            {
                              text: 'Guarantor Waiver+LDR',
                              value: 'Event Process;Auto Enroll',
                            },
                            {
                              text: 'Rent Guaranty',
                              value: 'Auto Enroll',
                            },
                          ]}
                          value={selectedBType}
                          multiselect={true}
                          showSearch={false}
                          placeholder="Property Type"
                          label="Property Type"
                          style={{
                            width: '100%',
                          }}
                        />

                        <Dropdown
                          onChange={(e) => {
                            setIsSHouse(e.target.value);
                          }}
                          options={BOOLEAN_YES_NO_OPTIONS}
                          value={isSHouse}
                          multiselect={true}
                          showSearch={false}
                          placeholder="Student Housing"
                          label="Student Housing"
                          style={{
                            width: '100%',
                          }}
                        />
                      </DropFilterContainer>
                    ),
                    resetFunc: resetFunc,
                    applyFunc: applyFunc,
                  }}
                  pagination={{
                    ...pagination,
                    totalItems: buildingsJSON.total,
                    totalPages: Math.ceil(buildingsJSON.total / pagination.rowsPerPage),
                  }}
                  onChangePagination={setPagination}
                  sortOptions={sortOptions}
                  onChangeSort={setSortOptions}
                  style={{ width: '100%' }}
                  title="Properties"
                  columns={delegatedBuildingColumns}
                />
                <ListExportScopeModal
                  open={userDetailExportOpen}
                  onClose={() => setUserDetailExportOpen(false)}
                  tableRef={userDetailTableRef}
                  title="Properties"
                  currentPageRowCount={tableData.length}
                  paginationTotalItems={buildingsJSON.total}
                  fullDatasetTotal={userDetailFullExportTotal}
                  enableFiltered={Boolean(activeDetail?.property)}
                  enableFull={Boolean(activeDetail?.property)}
                  enableSelected={false}
                />
              </CustomBox>
            </React.Fragment>
          </Box>
        </React.Fragment>
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;

const CustomTypography = styled(Typography)`
  word-break: break-all;
  overflow-wrap: anywhere;
`;
